import { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Zap, ArrowRight, CheckCircle2, Loader2, ShieldAlert } from "lucide-react";
import { motion } from "framer-motion";
import SEO from "@/components/SEO";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QUESTIONS = [
  {
    id: "stack",
    label: "Primary language / stack of the legacy system",
    options: [
      { value: "cobol", label: "COBOL / Mainframe" },
      { value: "natural_adabas", label: "Natural / Adabas" },
      { value: "pl1", label: "PL/1" },
      { value: "java", label: "Legacy Java (J2EE, Struts, etc.)" },
      { value: "dotnet", label: ".NET Framework (pre-Core)" },
      { value: "other", label: "Other / Mixed" },
    ],
  },
  {
    id: "size",
    label: "Approximate codebase size",
    options: [
      { value: "lt_100k", label: "< 100k lines" },
      { value: "100k_1m", label: "100k – 1M lines" },
      { value: "1m_10m", label: "1M – 10M lines" },
      { value: "gt_10m", label: "> 10M lines" },
    ],
  },
  {
    id: "urgency",
    label: "How urgent is modernization?",
    options: [
      { value: "exploring", label: "Exploring / researching" },
      { value: "6_12_months", label: "Planned for 6–12 months" },
      { value: "lt_6_months", label: "Planned within 6 months" },
      { value: "underway", label: "Already underway" },
    ],
  },
  {
    id: "vendor",
    label: "Are you working with a modernization vendor?",
    options: [
      { value: "none", label: "No vendor yet" },
      { value: "happy", label: "Yes, happy with them" },
      { value: "concerned", label: "Yes, but concerned" },
      { value: "considering", label: "Considering vendors now" },
    ],
  },
  {
    id: "risk",
    label: "Biggest risk concern",
    options: [
      { value: "regression", label: "Behavior regression" },
      { value: "cost", label: "Cost overrun" },
      { value: "timeline", label: "Timeline slip" },
      { value: "compliance", label: "Compliance / audit exposure" },
      { value: "all", label: "All of the above" },
    ],
  },
];

const BAND_COLOR = {
  Low: "text-neon-green border-neon-green/50",
  Moderate: "text-neon-cyan border-neon-cyan/50",
  Elevated: "text-yellow-400 border-yellow-400/50",
  High: "text-red-400 border-red-400/50",
};

export default function Audit() {
  const [step, setStep] = useState(0); // 0..4 questions, 5 = contact, 6 = result
  const [answers, setAnswers] = useState({});
  const [contact, setContact] = useState({ name: "", email: "", company: "" });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const total = QUESTIONS.length + 1; // +1 for contact step
  const progress = Math.min(((step + 1) / (total + 1)) * 100, 100);

  const selectAnswer = (val) => {
    const q = QUESTIONS[step];
    setAnswers((a) => ({ ...a, [q.id]: val }));
    setStep((s) => s + 1);
  };

  const submit = async (e) => {
    e?.preventDefault();
    if (submitting) return;
    if (!contact.name || !contact.email) {
      toast.error("Please share your name and work email.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/audit`, {
        name: contact.name,
        email: contact.email,
        company: contact.company,
        answers,
      });
      if (res.data?.success) {
        setResult(res.data.result);
        setStep(QUESTIONS.length + 1);
        toast.success("Risk audit generated.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not generate audit. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setStep(0);
    setAnswers({});
    setContact({ name: "", email: "", company: "" });
    setResult(null);
  };

  const atQuestion = step < QUESTIONS.length;
  const atContact = step === QUESTIONS.length;
  const atResult = step === QUESTIONS.length + 1 && result;

  return (
    <>
      <SEO
        title="Free Modernization Risk Audit"
        description="A free 60-second risk audit for your legacy modernization programme. Get a tailored risk score, findings and recommendations, instantly."
        path="/audit"
      />

      {/* Header */}
      <section className="relative border-b border-white/10 bg-obsidian">
        <div className="absolute inset-0 bg-grid opacity-30" />
        <div className="relative max-w-4xl mx-auto px-5 lg:px-8 py-16 lg:py-20">
          <div className="inline-flex items-center gap-2 chip text-neon-green border border-neon-green/40 px-2.5 py-1">
            <Zap size={12} /> free.lead_magnet
          </div>
          <h1 className="mt-4 font-display font-black text-4xl md:text-5xl lg:text-6xl text-white tracking-tighter leading-[0.95]">
            Modernization Risk Audit
          </h1>
          <p className="mt-5 max-w-2xl text-white/70 text-base leading-relaxed">
            Answer 5 quick questions. Get an instant, personalised risk score,
            findings, and recommendations for your legacy modernization
            programme. No sales call required.
          </p>
        </div>
      </section>

      {/* Wizard */}
      <section className="bg-obsidian border-b border-white/10">
        <div className="max-w-3xl mx-auto px-5 lg:px-8 py-16">
          {/* Progress */}
          {!atResult && (
            <div className="mb-10">
              <div className="flex items-center justify-between mb-3 font-mono text-xs text-white/50">
                <span>
                  step {Math.min(step + 1, total)} / {total}
                </span>
                <span>{Math.round(progress)}%</span>
              </div>
              <div className="h-1 bg-white/10 relative overflow-hidden">
                <div
                  className="h-full bg-neon-green transition-all duration-500"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          )}

          {/* Question */}
          {atQuestion && (
            <motion.div
              key={`q-${step}`}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              className="border border-white/10 bg-carbon p-8 lg:p-10"
              data-testid={`audit-step-${step}`}
            >
              <div className="chip text-neon-green">
                Q.0{step + 1} / 0{QUESTIONS.length}
              </div>
              <h2 className="mt-3 font-display font-bold text-white text-2xl md:text-3xl leading-tight">
                {QUESTIONS[step].label}
              </h2>

              <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {QUESTIONS[step].options.map((o) => (
                  <button
                    key={o.value}
                    onClick={() => selectAnswer(o.value)}
                    className="text-left border border-white/15 p-4 hover:border-neon-green hover:bg-[#0b0b0b] hover:text-neon-green transition-all group"
                    data-testid={`audit-option-${QUESTIONS[step].id}-${o.value}`}
                  >
                    <span className="font-mono text-sm text-white group-hover:text-neon-green">
                      {o.label}
                    </span>
                  </button>
                ))}
              </div>

              {step > 0 && (
                <button
                  onClick={() => setStep((s) => s - 1)}
                  className="mt-8 text-sm font-mono text-white/50 hover:text-white"
                  data-testid="audit-back-button"
                >
                  ← back
                </button>
              )}
            </motion.div>
          )}

          {/* Contact step */}
          {atContact && (
            <motion.form
              onSubmit={submit}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              className="border border-white/10 bg-carbon p-8 lg:p-10 space-y-6"
              data-testid="audit-contact-step"
            >
              <div className="chip text-neon-green">Q.06, last step</div>
              <h2 className="mt-1 font-display font-bold text-white text-2xl md:text-3xl leading-tight">
                Where should we send your audit?
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Field label="Name" required>
                  <input
                    value={contact.name}
                    onChange={(e) => setContact({ ...contact, name: e.target.value })}
                    className="audit-input"
                    placeholder="Jane Doe"
                    data-testid="audit-field-name"
                  />
                </Field>
                <Field label="Work email" required>
                  <input
                    type="email"
                    value={contact.email}
                    onChange={(e) => setContact({ ...contact, email: e.target.value })}
                    className="audit-input"
                    placeholder="jane@company.com"
                    data-testid="audit-field-email"
                  />
                </Field>
              </div>
              <Field label="Company">
                <input
                  value={contact.company}
                  onChange={(e) => setContact({ ...contact, company: e.target.value })}
                  className="audit-input"
                  placeholder="Acme Corp"
                  data-testid="audit-field-company"
                />
              </Field>

              <div className="flex items-center justify-between pt-3 border-t border-white/10">
                <button
                  type="button"
                  onClick={() => setStep((s) => s - 1)}
                  className="text-sm font-mono text-white/50 hover:text-white"
                >
                  ← back
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="audit-submit"
                  className="inline-flex items-center gap-2 bg-neon-green text-obsidian px-6 py-3 text-sm font-mono font-bold uppercase tracking-widest hover:shadow-[0_0_40px_rgba(0,255,65,0.6)] disabled:opacity-60"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="animate-spin" size={14} /> Generating…
                    </>
                  ) : (
                    <>
                      Generate My Audit <ArrowRight size={14} />
                    </>
                  )}
                </button>
              </div>
              <p className="text-xs font-mono text-white/40">
                We'll never sell your details. One human reply, if useful.
              </p>
            </motion.form>
          )}

          {/* Result */}
          {atResult && (
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              className="border border-white/10 bg-carbon p-8 lg:p-12"
              data-testid="audit-result"
            >
              <div className="chip text-neon-green">&gt; /audit_report</div>
              <div className="mt-6 flex flex-col md:flex-row md:items-center gap-6 md:gap-10">
                <div
                  className={`h-32 w-32 border ${BAND_COLOR[result.risk_band]} flex flex-col items-center justify-center shrink-0`}
                  data-testid="audit-score"
                >
                  <span className="font-display font-black text-5xl leading-none">
                    {result.risk_score}
                  </span>
                  <span className="mt-1 text-[10px] font-mono uppercase tracking-[0.2em] text-white/60">
                    / 100
                  </span>
                </div>
                <div className="flex-1">
                  <div className={`inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest border px-3 py-1 ${BAND_COLOR[result.risk_band]}`}>
                    <ShieldAlert size={12} /> {result.risk_band} risk
                  </div>
                  <h2 className="mt-3 font-display font-black text-2xl md:text-3xl text-white leading-tight">
                    {result.headline}
                  </h2>
                </div>
              </div>

              <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="border border-white/10 bg-obsidian p-6">
                  <div className="chip text-neon-cyan">// findings</div>
                  <ul className="mt-4 space-y-3">
                    {result.findings.map((f, i) => (
                      <li key={i} className="flex items-start gap-3 text-sm text-white/80">
                        <span className="mt-1.5 h-1.5 w-1.5 bg-neon-cyan shrink-0" />
                        {f}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="border border-white/10 bg-obsidian p-6">
                  <div className="chip text-neon-green">// recommendations</div>
                  <ul className="mt-4 space-y-3">
                    {result.recommendations.map((r, i) => (
                      <li key={i} className="flex items-start gap-3 text-sm text-white/80">
                        <CheckCircle2 size={14} className="text-neon-green mt-0.5 shrink-0" />
                        {r}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="mt-10 flex flex-wrap gap-4 pt-8 border-t border-white/10">
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 bg-neon-green text-obsidian px-6 py-3 text-sm font-mono font-bold uppercase tracking-widest hover:shadow-[0_0_40px_rgba(0,255,65,0.6)]"
                  data-testid="audit-cta-contact"
                >
                  Talk to the founder <ArrowRight size={14} />
                </Link>
                <Link
                  to="/products/legacy-testing-suite"
                  className="inline-flex items-center gap-2 border border-white/20 text-white px-6 py-3 text-sm font-mono uppercase tracking-widest hover:border-neon-cyan hover:text-neon-cyan"
                >
                  See the testing suite
                </Link>
                <button
                  onClick={reset}
                  className="text-sm font-mono text-white/50 hover:text-white ml-auto"
                  data-testid="audit-reset"
                >
                  take the audit again →
                </button>
              </div>
            </motion.div>
          )}
        </div>
      </section>

      <style>{`
        .audit-input {
          width: 100%;
          background: #050505;
          border: 1px solid rgba(255,255,255,0.14);
          color: #fff;
          padding: 12px 14px;
          font-family: 'JetBrains Mono', monospace;
          font-size: 14px;
          outline: none;
          transition: border-color 0.2s, box-shadow 0.2s;
        }
        .audit-input::placeholder { color: rgba(255,255,255,0.3); }
        .audit-input:focus {
          border-color: #00FFFF;
          box-shadow: 0 0 0 1px #00FFFF, 0 0 22px rgba(0,255,255,0.25);
        }
      `}</style>
    </>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="chip text-white/60">
        {label} {required && <span className="text-neon-green">*</span>}
      </span>
      <div className="mt-2">{children}</div>
    </label>
  );
}
